import { Component, OnDestroy, OnInit } from '@angular/core';
import { AttendanceService } from '../../attendance.service';
import { EmployeeAttendance, EmployeeAttendanceDetails, PresenceStatus } from '../../attendance.model';
import { getDefaultProfilePicUrl } from '../../../utils';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { EmployeesService } from '../../../employees/employees.service';
import { EmployeeBasicInfo } from '../../../employees/employees.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AttendanceReportModalComponent } from '../attendance-report-modal/attendance-report-modal.component';

@Component({
  selector: 'app-attendance',
  standalone: false,
  templateUrl: './attendance.component.html',
  styleUrl: './attendance.component.scss',
})
export class AttendanceComponent implements OnInit, OnDestroy {
  filters = {
    employeeId: null as string | null,
    fromDate: '',
    toDate: '',
  };

  attendanceList: EmployeeAttendanceDetails[] = [];
  employeeList: EmployeeBasicInfo[] = [];

  employeesTodayAttendance: EmployeeAttendance[] = [];
  checkInList: EmployeeAttendance[] = [];
  checkOutList: EmployeeAttendance[] = [];
  yetToCheckInList: EmployeeAttendance[] = [];
  isHistoryMode = false;
  isDataLoading: boolean = true;
  isHistoryDataLoading: boolean = false;
  isExportingMonthlyAttendance: boolean = false;

  exportMonth: number = new Date().getMonth() + 1;
  exportYear: number = new Date().getFullYear();

  private _attendanceSubscription!: Subscription;

  protected readonly getDefaultProfilePicUrl = getDefaultProfilePicUrl;

  protected readonly monthOptions = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];

  constructor(
    private _attendanceService: AttendanceService,
    private _employeeService: EmployeesService,
    private _modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this._loadEmployeeAttendanceData();
  }

  // Protected methods

  protected onFilterAttendance() {
    if (!this.filters.employeeId) {
      return;
    }

    this.isHistoryMode = true;

    const params: Record<string, string> = {};

    if (this.filters.fromDate) {
      params['start_date'] = this.filters.fromDate;
    }

    if (this.filters.toDate) {
      params['end_date'] = this.filters.toDate;
    }

    // Call API or filter data
    this._fetchFilteredAttendance(this.filters.employeeId!, params);
  }

  protected onDownloadAttendanceReport(): void {
    this._modalService.open(AttendanceReportModalComponent, {
      size: 'md',
      backdrop: 'static',
    });
  }

  protected onExportMonthlyAttendance(): void {
    this.isExportingMonthlyAttendance = true;

    this._employeeService
      .exportMonthlyAttendance(this.exportMonth, this.exportYear)
      .pipe(finalize(() => (this.isExportingMonthlyAttendance = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `attendance_${this.exportYear}_${String(this.exportMonth).padStart(2, '0')}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        },
      });
  }

  protected onClearFilters(): void {
    this.filters = {
      employeeId: null,
      fromDate: '',
      toDate: '',
    };
    this.isHistoryMode = false;
  }

  // Private methods

  private _fetchFilteredAttendance(employeeId: string, params = {}): void {
    if (!employeeId) {
      return;
    }

    this.isHistoryDataLoading = true;
    this._attendanceSubscription = this._attendanceService
      .employee_attendance(employeeId, params)
      .pipe(finalize(() => (this.isHistoryDataLoading = false)))
      .subscribe({
        next: (result: EmployeeAttendanceDetails[]) => {
          this.attendanceList = result;
        },
      });
  }

  private _loadEmployeeAttendanceData(): void {
    this.isDataLoading = true;

    this._attendanceSubscription = forkJoin({
      employeeListResponse: this._employeeService.lighter_list(),
      todaysAttendanceRecord: this._attendanceService.today_attendance(),
    })
      .pipe(
        finalize(() => {
          this.isDataLoading = false;
        })
      )
      .subscribe({
        next: ({ employeeListResponse, todaysAttendanceRecord }) => {
          // Set employee list
          this.employeeList = employeeListResponse;
          this.employeesTodayAttendance = todaysAttendanceRecord;

          // Categorize
          this.checkInList = this.employeesTodayAttendance.filter(emp => emp.status === PresenceStatus.CHECK_IN);
          this.checkOutList = this.employeesTodayAttendance.filter(emp => emp.status === PresenceStatus.CHECK_OUT);
          this.yetToCheckInList = this.employeesTodayAttendance.filter(
            emp => emp.status === PresenceStatus.YET_TO_CHECK_IN
          );
        },
      });
  }

  ngOnDestroy(): void {
    this._attendanceSubscription?.unsubscribe();
  }
}
